import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import {
  of,
  Subject,
  BehaviorSubject
} from 'rxjs';
import {
  map,
  startWith,
  catchError, distinctUntilChanged,
  switchMap,
  tap,
  takeUntil
} from 'rxjs/operators';
import Swal from 'sweetalert2';

import { DefaultRequestParamsConstant } from '@app/core/_constant/index.constants';
import { InventoryService } from '@app/core/inventory/services/inventory.service';
import { LocationService } from '@app/core/locations/services/location.service';
import { RfqService } from '@app/core/quotes';
import { RfqLotService } from '@app/core/rfq-lots/services/rfq-lots.service';
import { EFeatureFlag } from '@app/feature-toggle-list';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';

@Component({
  selector: 'cnc-adjustment-entry-stock-locations-form',
  templateUrl: './adjustment-entry-stock-locations-form.component.html',
  styleUrls: ['./adjustment-entry-stock-locations-form.component.scss'],
})
export class AdjustmentEntryStockLocationsFormComponent
  implements OnInit, OnDestroy
{
  pageTitle :string = 'Add stock locations';
  loading :boolean;

  location$ = new BehaviorSubject<any[]>([]);
  locationLoading = false;
  locationInput$ = new BehaviorSubject<string>('');
  currentPage: number = 1;
  lastPage: number = 1;

  rfqLots :any[] = [];
  ftLotTracking :boolean = false;
  rfqLotTracking :boolean = false;

  locationIds :any[] = [];
  lotId :number = null;
  stockId :number = null;

  rfqLocationStocks :any[] = [];

  @Input() stock :any;
  @Input() locationId :number | null = null;
  @Input() rfqId :number | null = null;
  @Input() warehouseId :number | null = null;

  private onDestroy$ = new Subject();

  constructor(
    public rfqService :RfqService,
    public activeModal :NgbActiveModal,
    private toastr :ToastrService,
    public invSvc :InventoryService,
    private locationSvc :LocationService,
    public rfqLotSvc :RfqLotService,
    private ft :FeatureToggleService,
  ) {
    this.ftLotTracking = this.ft.featureEnabled(EFeatureFlag.LOT_TRACKING);
  }

  ngOnInit() {
    if (this.stock) {
      if (this.stock?.rfq_id) {
        this.rfqId = this.stock.rfq_id;
      }

      if (this.stock?.warehouse_id) {
        this.warehouseId = this.stock.warehouse_id;
      }

      if (this.stock?.id) {
        this.stockId = this.stock.id;
      }
    }

    this.getRfq();
    this.getLocations();
    this.getRfqLots();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  save() :void {
    if (this.locationId) {
      this.locationIds.push(this.locationId);
    }

    if (this.locationIds.length === 0) {
      Swal.fire('Error', 'At least one location is required.', 'error');

      return;
    }

    if (this.ftLotTracking && this.rfqLotTracking && this.lotId === null) {
      Swal.fire('Error', 'Lot number is required.', 'error');

      return;
    }

    if (!this.stockId) {
      const rfqLocationStock = this.rfqLocationStocks.find(
        (rfqLocationStock) => rfqLocationStock.warehouse_id === this.warehouseId,
      );

      if (rfqLocationStock) {
        this.stockId = rfqLocationStock.id;
      } else {
        this.stockId = 0;
      }
    }

    const payload :any = {
      location_ids: this.locationIds,
      warehouse_id: this.warehouseId,
      lot_id: this.lotId,
      rfq_id: this.rfqId,
    };
    this.invSvc
      .addStockLocations(this.stockId, payload)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (resp :any) => {
          this.toastr.success('Locations added.', 'Success');
          this.activeModal.close(resp);
        },
        (error) => {
          this.toastr.error('Save error. Please try again.', 'Error');
        },
      );
  }

  onScrollToEnd() :void {
    if (this.currentPage < this.lastPage) {
      this.currentPage++;
      this.getLocations();
    }
  }

  private getLocations() :void {
    this.locationInput$.pipe(
      startWith(''),
      distinctUntilChanged(),
      tap((term) => {
        this.locationLoading = true;
        // Reset to first page only when there's a search term
        if (term && term.trim() !== '') {
          this.currentPage = 1;
          this.location$.next([]);
        }
      }),
      switchMap((term) =>
        this.locationSvc
          .getAsync({
            keyword: term,
            parent_id: this.warehouseId,
            per_page: DefaultRequestParamsConstant.limit,
            page: this.currentPage,
            withoutStockLocations: true,
          })
          .pipe(
            catchError(() => of({ data: [] })),
            map((resp :any) => {
              if (resp.data) {
                const currentItems = this.location$.getValue();
                if (this.currentPage === 1) {
                  this.location$.next(resp.data);
                } else {
                  const newItems = resp.data.filter(
                    (newItem: any) => !currentItems.some(
                      (existingItem: any) => existingItem.id === newItem.id
                    )
                  );
                  this.location$.next([...currentItems, ...newItems]);
                }
                this.lastPage = resp.last_page || 1;
              }
              return this.location$.getValue();
            }),
            tap(() => {
              this.locationLoading = false;
            }),
          ),
      ),
      takeUntil(this.onDestroy$),
    ).subscribe();
  }

  private getRfqLots() :void {
    this.rfqLotSvc
      .getByRfqIds({
        rfqIds: [this.rfqId],
        limit: 1000,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((resp) => {
        if (resp.data) {
          this.rfqLots = resp.data;
        }
      });
  }

  private getRfq() :void {
    this.rfqService
      .getById(this.rfqId, true)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((resp :any) => {
        this.rfqLotTracking = resp.lot_tracking;
        this.rfqLocationStocks = resp.location_stocks;
      });
  }
}
